import { ChangeDetectorRef, Directive, Input } from '@angular/core';
import { Store } from '@ngxs/store';
import type { ActionDef } from '@ngxs/store/src/actions/symbols';
import { ComboBoxComponent, FilterableComponent, MultiSelectComponent } from '@progress/kendo-angular-dropdowns';
import { Changeable, DestroyService, provideDestroy$ } from '@technocom/common';
import type { NgChangeMap, Nil } from '@technocom/common/platform';
import { finalize, takeUntil } from 'rxjs';

/**
 * Директива для привязки отправления запроса {@link ActionDef} через {@link Store}.
 */
@Directive({
  selector: '[appDropDownDataBinding]',
  providers: [provideDestroy$()],
})
export class DropDownDataBindingDirective<TAction extends ActionDef> extends Changeable {
  /**
   * Класс действия для создания запроса в {@link Store}.
   */
  @Input('appDropDownDataBinding')
  actionType: TAction;

  /**
   * Входной параметр конструктора {@link actionType}.
   */
  @Input()
  actionPayload: ConstructorParameters<TAction>[0] | Nil;

  /**
   * Флаг активации выполнения запроса.
   *
   * - Значение `boolean` определяет выполнять запрос или нет.
   * - Значение `auto` означает выполнение запроса только если хост-компонент активен
   * ({@link host.disabled} !== `false`).
   * - Значение {@link Nil} (по умолчанию) означает выполнять запрос всегда.
   */
  @Input()
  actionIf: boolean | 'auto' | Nil;

  @Input()
  allowCustom: boolean;

  get host(): DropdownLikeComponent {
    return this.filterableComponent as DropdownLikeComponent;
  }

  constructor(
    private filterableComponent: FilterableComponent,
    private cd: ChangeDetectorRef,
    private store: Store,
    protected destroy$: DestroyService,
  ) {
    super();

    const host = this.host;

    this.allowCustom = host.allowCustom;

    // use allowCustom option as a workaround for https://github.com/telerik/kendo-angular/issues/3532
    if (host instanceof ComboBoxComponent || host instanceof MultiSelectComponent) {
      host.allowCustom = true;
    }
  }

  private _dispatched = false;

  isEnabled(): boolean {
    const mode = this.actionIf;

    return mode === 'auto' ? !this.host.disabled : mode !== false;
  }
  isPending(): boolean {
    return !this._dispatched;
  }

  protected rebind(): void {
    if (this.isPending() && this.isEnabled()) {
      this._dispatched = true;

      const widget = this.host;
      const repairBinding = !this.isRendered();
      const changeDetector = this.cd;

      widget.loading = true;
      changeDetector.markForCheck();

      this.store
        .dispatch(new this.actionType(this.actionPayload))
        .pipe(
          finalize(() => {
            widget.loading = false;

            if (repairBinding) {
              widget.allowCustom = !!this.allowCustom;
            }

            changeDetector.markForCheck();
          }),
          takeUntil(this.destroy$),
        )
        .subscribe(() => {
          // this._dispatched = true;
        });
    }
  }

  protected override onInit(): void {
    super.onInit();

    this.rebind();
  }
  protected override onChanges(changes: NgChangeMap<this>): void {
    super.onChanges(changes);

    if (this.isRendered()) {
      const actionChanged = !!(changes.actionType || changes.actionPayload);

      if (actionChanged || changes.actionIf) {
        if (actionChanged) {
          this._dispatched = false;
        }

        this.rebind();
      }
    }
  }
}

export interface DropdownLikeComponent extends FilterableComponent {
  loading: boolean;
  disabled: boolean;
  allowCustom?: boolean;
}
